/**
 * What `Curve2D::_set_data` and `Curve3D::_set_data` accept in `_data`: the keys they
 * require and the point layout they refuse otherwise. The decoders and the path
 * linters share it, so a curve the linter calls unloadable is one the preview leaves
 * empty. No THREE.
 */
import { dictKey, packedArrayForms } from '../../../godot/variant/variantParser.js';
import { dictPackedField, packedFloatCount } from '../../../godot/variant/packedArrayFields.js';
function bezierDataFormat(className, refusedAt, vectorSize, otherRequiredKeys) {
    const pointsType = `PackedVector${vectorSize}Array`;
    return {
        className,
        refusedAt,
        floatsPerPoint: vectorSize * 3,
        readPointsField: dictPackedField('points', pointsType),
        pointsForms: packedArrayForms(pointsType),
        vectorSize,
        otherRequiredKeys: otherRequiredKeys.map((key) => ({
            key,
            present: dictKey(key),
        })),
    };
}
/** `Curve2D::_set_data` (curve.cpp:1238-1259). */
export const CURVE2D_DATA = bezierDataFormat('Curve2D', 'curve.cpp:1239-1243', 2, []);
/** `Curve3D::_set_data` (curve.cpp:2278-2299) also requires `"tilts"` (curve.cpp:2280). */
export const CURVE3D_DATA = bezierDataFormat('Curve3D', 'curve.cpp:2279-2284', 3, ['tilts']);
/**
 * Read `data` as `_set_data` does. It fails on a missing key, then on `pc % 3 != 0`,
 * where `pc` counts vectors (curve.cpp:1239-1243, 2279-2284). Either way it returns
 * before it touches the point list.
 */
export function readBezierData(data, format) {
    const points = format.readPointsField(data);
    if (points === null)
        return { refusal: { kind: 'missing-key', key: 'points' } };
    const missing = format.otherRequiredKeys.find(({ present }) => !present.test(data));
    if (missing !== undefined)
        return { refusal: { kind: 'missing-key', key: missing.key } };
    // The flat constructor builds `args.size() / vectorSize` vectors (variant_parser.cpp:1555,
    // 1573), so a trailing partial vector is dropped before `_set_data` counts `pc`.
    const floats = packedFloatCount(format.pointsForms, points, format.vectorSize);
    const vectors = Math.floor(floats / format.vectorSize);
    if (vectors % 3 !== 0)
        return { refusal: { kind: 'partial-point', floats } };
    return { refusal: null, loaded: { points, controlPoints: vectors / 3 } };
}
/** The sentence a diagnostic gives for `refusal`, naming its consequence. */
export function bezierRefusalProblem(refusal, format) {
    const { className, refusedAt, floatsPerPoint } = format;
    if (refusal.kind === 'missing-key') {
        return (`its ${className} has no "${refusal.key}" in \`_data\`. Godot requires it (${refusedAt}) ` +
            'and loads the curve with zero points, so the path draws nothing.');
    }
    return (`its ${className} "points" holds ${refusal.floats} floats. Godot needs a whole number of ` +
        `control points at ${floatsPerPoint} floats each (in / out / position) and loads the curve ` +
        'with zero points otherwise.');
}
//# sourceMappingURL=bezierData.js.map